// components/SortableRow.js
import React, { memo, useCallback, useEffect } from "react";
import {
  Box,
  IconButton,
  Avatar,
  LinearProgress,
  Typography,
  Paper,
  Stack,
} from "@mui/material";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  StyledCheckboxInput,
  StyledTextInput,
} from "../../../../../../Reuse/FormInput";
import ClearRoundedIcon from "@mui/icons-material/ClearRounded";
import { CheckValue } from "../../../../../../Service/service";

const SortableRow = ({ id, field, index, onRemove, onRoofToggle }) => {
  const { control, setValue } = useFormContext();
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id });
  const floorPlan = useWatch({ control, name: `floorPlans.${index}` });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const handleCheckboxChange = useCallback(() => {
    onRoofToggle(index, !floorPlan.isRoof);
  }, [index, floorPlan.isRoof, onRoofToggle]);

  const handleRemoveClick = useCallback(() => {
    onRemove(index);
  }, [index, onRemove]);

  const handleLabelChange = useCallback(
    (e, onChange) => {
      setValue(`floorPlans.${index}.isLabelCustom`, true);
      onChange(e);
    },
    [index, setValue]
  );

  return (
    <Stack
      gap={2}
      flexDirection={"row"}
      sx={{
        alignItems: "center",
      }}
    >
      <Box sx={{ minWidth: 133 }}>
        <StyledTextInput
          name={`floorPlans.${index}.label`}
          control={control}
          placeholder={"Enter Floor Name"}
          showLabel={false}
          rules={{ required: "Floor name is required" }}
          onChange={handleLabelChange}
        />
      </Box>
      <Paper
        ref={setNodeRef}
        elevation={1}
        component={Stack}
        style={style}
        {...attributes}
        sx={{
          border: "1px solid #ccc",
          borderRadius: 2,
          p: 0.5,
          flexGrow: 1,
          alignItems: "center",
          flexDirection: "row",
          bgcolor: "background.paper",
          width: "100%",
          gap: 2,
          height: 88,
          minWidth: 0,
        }}
      >
        <Avatar
          variant="square"
          src={floorPlan.previewUrl}
          sx={{
            width: 120,
            borderRadius: 0.75,
            height: "100%",
            [`& .MuiAvatar-img`]: {
              objectFit: "contain",
              background: "#000000",
            },
          }}
        />
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          {!CheckValue(floorPlan.serverId) ? (
            <>
              <Stack
                direction={"row"}
                justifyContent={"space-between"}
                alignItems={"center"}
              >
                <Typography
                  variant="body1"
                  sx={(theme) => ({
                    ...theme.mixins.overflow,
                    maxWidth: "80%",
                  })}
                >
                  {floorPlan.file?.name}
                </Typography>
                {floorPlan.uploadProgress >= 100 ? (
                  <Typography
                    variant="body1"
                    sx={{ color: "#099250", fontWeight: 600 }}
                  >
                    {"Uploaded"}
                  </Typography>
                ) : (
                  <Typography variant="body1">
                    {Math.round(floorPlan.uploadProgress)}%
                  </Typography>
                )}
              </Stack>
              <LinearProgress
                variant="determinate"
                value={floorPlan.uploadProgress}
                sx={{
                  [`& .MuiLinearProgress-bar1`]: {
                    color: "#101828",
                  },
                }}
              />
            </>
          ) : null}
          <StyledCheckboxInput
            value={floorPlan.isRoof}
            handleCheckboxChange={handleCheckboxChange}
            label="Mark as Campus Roof"
          />
        </Box>
        <IconButton size="small" onClick={handleRemoveClick}>
          <ClearRoundedIcon fontSize="small" sx={{ color: "#E62E05" }} />
        </IconButton>
        <Box {...listeners} sx={{ cursor: "grab" }}>
          <IconButton size="small">
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </Box>
      </Paper>
    </Stack>
  );
};

export default SortableRow;
